/**
 * Parent help copy, keyed by ROUTE rather than by title.
 *
 * Same discipline `help-copy-trfer.ts` established (#611): the route is the
 * stable identity, so a rename carries its documentation along automatically
 * and only a genuinely NEW surface can arrive undocumented — which is what
 * `HelpPage.navParity.test.tsx` fails the build over. The parent list was the
 * last hand-authored copy of the sidebar left on the page; it lived in
 * `HelpPage.tsx` and had already drifted once, describing "Medical" months
 * after the page became Health.
 *
 * Each description takes the reader's OWN companion persona. A family whose
 * companion is Maya must never read about Ember, which is why every entry
 * resolves with `{{companion}}` as a param (#639).
 *
 * #2109 R3-B25: the map stays route-keyed (`HelpPage.navParity.test.tsx` and
 * the provenance guard's `COPY_MODULES` both depend on it); only the VALUES
 * became `help:parent.<route>.{description,memorial}` catalog keys, resolved
 * at render by `helpCopyText`. Whether a card is withheld in the memorial
 * register (`memorial: null`) stays a code decision, here.
 */
import type { AnyTFunction, TKey } from '@/i18n/msg';

export interface ParentHelpCopyEntry {
  /**
   * The card's body text, as a catalog key. Resolved with the reader's
   * companion name; entries that do not mention the companion ignore it.
   */
  description: TKey;
  /**
   * The memorial-register replacement for `description` (#685/#693).
   *
   * `null` withholds the whole card. `undefined` means the card reads the same
   * in both registers. Kept beside the active-register copy rather than in a
   * second map so the two registers sit side by side in one read — the same
   * discipline `bereavement-tone.ts` uses, and the reason drift is visible
   * here instead of inferred from an absence.
   */
  memorial?: TKey | null;
}

/** A help-copy key in the active language, with the reader's companion name. */
export function helpCopyText(key: TKey, companionName: string, t: AnyTFunction): string {
  return String((t as unknown as (k: string, p: Record<string, unknown>) => string)(key, { companion: companionName }));
}

export const PARENT_HELP_COPY: Record<string, ParentHelpCopyEntry> = {
  '/dashboard': {
    // The home is the companion (2026-09-18, guideline §2.9): the composer,
    // a few ideas, and one thing she noticed — an appointment in the next two
    // days, an overdue to-do, a question TRF is asking, renewals while open.
    description: 'help:parent.dashboard.description',
    // Memorial mode keeps the composer and the questions from TRF, and
    // nothing else (#644), so the card promises only those.
    memorial: 'help:parent.dashboard.memorial',
  },
  '/medical': {
    // "Health", not "Medical": the page became HealthPage in 35601a57 and this
    // label was left behind, one of three that were. The sidebar entry is
    // already "Health" and one destination gets one noun (design grammar §4).
    description: 'help:parent.medical.description',
    // Withheld outright, and belt-and-braces: the parent nav already drops the
    // Health entry for a bereaved family (#693), and `HelpPage` builds its
    // cards from that same gated nav — but if the gate ever loosened, every
    // verb in the description above is an invitation to log treatment for a
    // child who has died. Deliberately not replaced with a memorial paragraph
    // either: HealthMemorialView says the care record is closed, and a help
    // card pointing at it would be a second invitation to go and read that.
    memorial: null,
  },
  // Collector copy (2026-09-10): TRF Grants first; Expenses & receipts is the
  // family's ledger — drop a receipt, we read it, the record goes to TRF when
  // it is complete.
  '/bills': {
    description: 'help:parent.bills.description',
  },
  '/tasks': {
    description: 'help:parent.tasks.description',
  },
  '/calendar': {
    description: 'help:parent.calendar.description',
    // CalendarPage hides treatment events in memorial mode and hides the
    // category legend along with them, so both halves of the sentence above
    // describe controls the reader does not have.
    memorial: 'help:parent.calendar.memorial',
  },
  '/community': {
    description: 'help:parent.community.description',
  },
  '/circle': {
    description: 'help:parent.circle.description',
  },
  '/settings': {
    // Conversations joined this card on 2026-09-21 when the thread archive
    // moved into the Companion tab. Help describing a surface that no longer
    // has its own page is the #611 drift this file exists to prevent, so the
    // sentence moves rather than disappearing.
    description: 'help:parent.settings.description',
  },
};
